import type { TelegramState } from "../api/types";

/** Which sentence the settings page is in the business of saying.
 *
 *  A module rather than a chain of ternaries inside the page, for the reason
 *  watchTarget.ts is one: this suite runs under `environment: "node"` and has
 *  no DOM to render a page into, so a decision left in the component is a
 *  decision no test can reach. The states are the substance of this page — a
 *  reader arrives at it precisely because something is not working — so they
 *  are decided here and the component only spells them out.
 */
export type LinkView =
  | { kind: "unconfigured" }
  | { kind: "unlinked" }
  | { kind: "linked"; chatId: number | null; linkedAt: number | null; canLink: boolean }
  | {
      kind: "blocked";
      chatId: number | null;
      linkedAt: number | null;
      reason: string;
      canLink: boolean;
    };

/** Which state this account is in.
 *
 *  **`configured` gates the mint path and nothing else.** It is the server's
 *  `linking_configured` — a bot token AND a webhook secret AND a public base
 *  URL — and those last two exist to register the webhook, which is how a
 *  *new* link is made. Sending needs none of them: `telegram.send_message`
 *  wants the token alone. So a deployment that keeps its token and loses its
 *  base URL goes on messaging every chat already linked, and a page that
 *  answered "this deployment has no Telegram bot configured" to the person
 *  being messaged would be telling them the opposite of what is happening —
 *  and, by hiding their chat, would take away the only control that stops it.
 *  An account's own link is therefore reported whatever `configured` says, and
 *  `canLink` carries the one thing that is actually false in that state: no
 *  new code can be minted, because the mint route answers 503.
 *
 *  `unconfigured` is what is left: not linked and unable to link, which is a
 *  page with genuinely nothing to offer.
 *
 *  The owner's webhook panel is deliberately not gated on any of this, and the
 *  asymmetry is the point rather than an oversight — `webhook-info` is gated on
 *  the bot token alone precisely so a half-configured deployment can still be
 *  diagnosed, which is the one state anybody would open it in.
 *
 *  `blocked` before `linked`, for a plainer reason: a link with `disabled_at`
 *  set is a chat that blocked the bot, nothing has been sent to it since, and
 *  a page reporting that as a working link is the one thing a reader cannot
 *  recover from on their own.
 */
export function linkView(state: TelegramState): LinkView {
  if (!state.linked) {
    return state.configured ? { kind: "unlinked" } : { kind: "unconfigured" };
  }
  const chat = {
    chatId: state.chat_id, linkedAt: state.linked_at, canLink: state.configured,
  };
  if (state.disabled_at !== null) {
    return { kind: "blocked", ...chat, reason: state.disabled_reason };
  }
  return { kind: "linked", ...chat };
}

/** What the reader types into Telegram, exactly.
 *
 *  Never `/link@thebot`, which is the form Telegram's own client writes in a
 *  group: the webhook matches the command itself and ignores that form
 *  entirely, so a code sent in a group is not answered at all. The page still
 *  says "a direct message", for the better reason that the bot's confirmation
 *  names the account it linked. The string is pinned by a test rather than
 *  assembled inline, so a later "improvement" to it has to argue with the test
 *  first.
 */
export function linkCommand(code: string): string {
  return `/link ${code}`;
}

/** The bot's handle as Telegram spells it, or null when this deployment could
 *  not learn it.
 *
 *  `bot_name` comes from getMe at boot and is "" whenever that call failed, so
 *  the page needs a vaguer sentence for the null: an instruction that names no
 *  bot cannot be followed, and one that pretends to name it is worse. The
 *  leading @ is added here because getMe answers with the bare username, and
 *  tolerated when it is already there so the page can never print "@@".
 */
export function botHandle(botName: string): string | null {
  const name = botName.trim().replace(/^@+/, "");
  return name === "" ? null : `@${name}`;
}

/** Why a link stopped working, in a sentence that survives an empty reason.
 *
 *  `disabled_reason` is Telegram's own words ("Forbidden: bot was blocked by
 *  the user"), carried through rather than translated: it is the only account
 *  anybody gets of what happened, and the notifier stores whatever the API
 *  said. It can also be empty, which is why the colon is conditional — a band
 *  ending in a dangling "stopped accepting messages:" reads as a truncated
 *  page rather than as a state.
 */
export function blockedMessage(reason: string): string {
  const said = reason.trim();
  const opening = "Telegram is no longer accepting messages for this chat";
  return said === "" ? `${opening}.` : `${opening}: ${said}`;
}

/** How long a minted code has left. */
export type CodeLife = { live: true; minutes: number } | { live: false };

/** A code is good for ten minutes (`telegram_links.CODE_TTL_MINUTES`), and
 *  this page is the only place that can say so. The bot's answer to a dead
 *  code is deliberately silent about the account — it has to be, since a code
 *  that reaches the wrong chat has proved nothing — so a reader who comes back
 *  from lunch and sends an expired code is told nothing at all about why it did
 *  not work.
 *
 *  Rounded, with a floor of one minute: "expires in 0 minutes" is not a
 *  sentence, and the overstatement it allows is under half a minute on a
 *  ten-minute window. `now` is injectable the way format.ts's `ago` is, which
 *  is what makes this readable without freezing a clock.
 */
export function codeLife(expiresAt: number, now: number = Date.now() / 1000): CodeLife {
  const left = expiresAt - now;
  if (left <= 0) return { live: false };
  return { live: true, minutes: Math.max(1, Math.round(left / 60)) };
}
